// Bazares y Expositores — panel de quien ORGANIZA el evento.
// Backend: apps/exhibitions (mcp_django). Todo lo que se ve aquí se DERIVA de
// 4 tablas (Edición · Expositor · Stand · Pago); nada se captura dos veces.
//
// Módulo per-org: default OFF en todo archetype, se enciende en
// `enabled_modules` de la org. Hoy solo mimercadito.

import { useCallback, useEffect, useState } from 'react';
import {
  CalendarDays, Users, Wallet, RefreshCw, AlertCircle, Store, Search, Inbox,
  Check, X, Loader2, Send, Plus, ChevronDown, ChevronRight, ListChecks, Images,
} from 'lucide-react';
import {
  bazaresApi as api,
  type EditionSales, type StandRow, type ExhibitorCard, type DebtRow,
  type Solicitud, type DecisionOut, type PagoDetalle,
  type InteresEdicion, type Interesado, type Etapa,
} from '../api';
import { MaterialPanel } from './MaterialPanel';

type Tab = 'ventas' | 'stands' | 'expositores' | 'cobrar' | 'solicitudes' | 'interesados' | 'material';

const money = (v: string | number, cur = 'MXN') =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: cur })
    .format(Number(v || 0));

// Recibe DOS formas y hay que distinguirlas:
//   · fecha sola  "2026-09-11"            → las ediciones
//   · datetime    "2026-08-31T12:00:00Z"  → `creado` / `actualizado` de una solicitud
//
// El `+ 'T00:00:00'` es necesario para la fecha sola: sin él, `new Date()` la
// interpreta como medianoche UTC y en México se pinta el día ANTERIOR. Pero
// pegárselo a un datetime produce "2026-08-31T12:00:00ZT00:00:00", que es la
// basura que salía como «Invalid Date» en la columna Última vez.
const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

const fecha = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(SOLO_FECHA.test(iso) ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '—';   // nunca «Invalid Date» en pantalla
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
    + (SOLO_FECHA.test(iso)
        ? ''
        // "Última vez" con la hora: saber que alguien escribió hace un rato,
        // y no solo qué día, es la mitad del dato.
        : ` · ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`);
};

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-center py-10 text-sm text-muted-foreground">{children}</div>
  );
}

/** Lo que pidió alguien YA aprobado/rechazado y nadie ha revisado. Ella lo
 *  aprueba o no con un mensaje que le llega a la persona por WhatsApp. */
function Novedades({ solicitud, onRevisado }: { solicitud: Solicitud; onRevisado: () => void }) {
  const nombre = (solicitud.nombre || '').split(' ')[0];
  const [modo, setModo] = useState<'aprobar' | 'rechazar' | null>(null);
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);

  const abrir = (m: 'aprobar' | 'rechazar') => {
    setModo(m);
    setError(null);
    setTexto(m === 'aprobar'
      ? `¡Hola${nombre ? ` ${nombre}` : ''}! Sí puedes llevar lo que nos comentaste. Cualquier duda, aquí estoy.`
      : `¡Hola${nombre ? ` ${nombre}` : ''}! Por espacio, esta vez no podemos autorizar lo que nos comentaste. Tu lugar sigue igual; cualquier duda, aquí estoy.`);
  };

  const responder = async () => {
    if (!modo || !texto.trim()) return;
    setOcupado(true);
    setError(null);
    try {
      const r = await api.responderNovedades(solicitud.id, modo, texto.trim());
      setResultado(r.envio?.enviado
        ? '✅ Le llegó tu respuesta por WhatsApp.'
        : `Quedó guardado, pero el WhatsApp no le llegó${r.envio?.error ? ` (${r.envio.error})` : ''}. El asistente se lo dirá en cuanto escriba.`);
      setTimeout(onRevisado, 2500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const soloRevisado = async () => {
    setOcupado(true);
    setError(null);
    try {
      await api.marcarNovedadesVistas(solicitud.id);
      onRevisado();
    } catch (e) {
      setError((e as Error).message);
      setOcupado(false);
    }
  };

  const boton = 'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs disabled:opacity-50';
  return (
    <div className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-foreground whitespace-normal">
      <div className="text-xs font-semibold text-amber-700 dark:text-amber-400">🆕 Pidió después de tu respuesta</div>
      <ul className="mt-1 space-y-1 text-sm">
        {(solicitud.curacion_novedades ?? []).map((n, i) => (
          <li key={`${n.en}-${i}`}>
            {n.texto}
            <span className="ml-1 text-xs text-muted-foreground">· {fecha(n.en)}</span>
          </li>
        ))}
      </ul>

      {resultado ? (
        <p className="mt-2 text-xs">{resultado}</p>
      ) : modo ? (
        <div className="mt-2 space-y-2">
          <label className="block text-xs text-muted-foreground">
            Esto le llega por WhatsApp (puedes cambiarlo):
          </label>
          <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3}
            className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground" />
          <div className="flex gap-1">
            <button onClick={() => void responder()} disabled={ocupado || !texto.trim()}
              className={`${boton} bg-primary text-primary-foreground`}>
              {ocupado ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {modo === 'aprobar' ? 'Aprobar y avisarle' : 'No aprobar y avisarle'}
            </button>
            <button onClick={() => setModo(null)} disabled={ocupado}
              className={`${boton} border border-border bg-card text-foreground hover:bg-muted`}>
              <X size={13} /> Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-1">
          <button onClick={() => abrir('aprobar')} disabled={ocupado}
            className={`${boton} bg-emerald-600 text-white hover:bg-emerald-700`}>
            <Check size={13} /> Aprobar
          </button>
          <button onClick={() => abrir('rechazar')} disabled={ocupado}
            className={`${boton} border border-destructive/40 text-destructive hover:bg-destructive/10`}>
            <X size={13} /> No aprobar
          </button>
          <button onClick={() => void soloRevisado()} disabled={ocupado}
            className={`${boton} border border-border bg-card text-muted-foreground hover:bg-muted`}>
            Solo marcar como revisado
          </button>
        </div>
      )}
      {error && <div className="mt-1 text-xs text-destructive">{error}</div>}
    </div>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <span>{msg}</span>
    </div>
  );
}

export function BazaresPage({ tab = 'ventas' }: { tab?: Tab }) {
  const [active, setActive] = useState<Tab>(tab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sales, setSales] = useState<EditionSales[]>([]);
  const [stands, setStands] = useState<StandRow[]>([]);
  const [selectedEdition, setSelectedEdition] = useState<EditionSales | null>(null);
  const [exhibitors, setExhibitors] = useState<ExhibitorCard[]>([]);
  const [debts, setDebts] = useState<DebtRow[]>([]);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [interes, setInteres] = useState<InteresEdicion[]>([]);
  const [q, setQ] = useState('');
  // Ventas por bazar: por default SOLO lo vigente. Con el histórico completo la
  // tabla parece un acumulado millonario ("parece que soy putrimillonaria pero
  // pues no"). El histórico sigue a un clic.
  const [verPasadas, setVerPasadas] = useState(false);

  const recargarSolicitudes = useCallback(async () => {
    try {
      setSolicitudes(await api.solicitudes(q));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (active === 'ventas') {
        const hoy = new Date().toISOString().slice(0, 10);
        setSales(await api.sales(100, verPasadas ? '' : hoy));
      }
      if (active === 'expositores') setExhibitors(await api.exhibitors(q));
      if (active === 'cobrar') setDebts(await api.debts());
      if (active === 'interesados') setInteres(await api.interes());
      if (active === 'solicitudes') {
        // Las ediciones se cargan junto con las solicitudes porque aprobar
        // exige decir A CUÁL entra: sin eso, "aprobado" no permite cobrar —
        // nadie sabe después de qué cuota hablar.
        const [sol, eds] = await Promise.all([
          api.solicitudes(q),
          sales.length ? Promise.resolve(sales) : api.sales(),
        ]);
        setSolicitudes(sol);
        if (!sales.length) setSales(eds);
      }
      if (active === 'stands') {
        const s = sales.length ? sales : await api.sales();
        if (!sales.length) setSales(s);
        const target = selectedEdition ?? s[0] ?? null;
        setSelectedEdition(target);
        setStands(target ? await api.stands(target.edition_id) : []);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [active, q, sales, selectedEdition, verPasadas]);

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [active]);

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'solicitudes', label: 'Solicitudes', icon: <Inbox size={16} /> },
    { key: 'interesados', label: 'Interesados por edición', icon: <ListChecks size={16} /> },
    { key: 'ventas', label: 'Ventas por bazar', icon: <CalendarDays size={16} /> },
    { key: 'stands', label: 'Stands', icon: <Store size={16} /> },
    { key: 'expositores', label: 'Expositores', icon: <Users size={16} /> },
    { key: 'cobrar', label: 'Por cobrar', icon: <Wallet size={16} /> },
    { key: 'material', label: 'Material', icon: <Images size={16} /> },
  ];

  // Quien pidió algo nuevo después de tu respuesta va hasta arriba: es lo que
  // se estaba quedando sin contestar (el rack de Gabriela, 08-oct).
  const tieneNovedad = (s: Solicitud) => (s.curacion_novedades?.length ?? 0) > 0;
  const conNovedad = solicitudes.filter(tieneNovedad).length;
  const ordenadas = [...solicitudes].sort((a, b) => Number(tieneNovedad(b)) - Number(tieneNovedad(a)));

  // Totales del encabezado — se derivan de lo ya cargado, sin otra llamada.
  const totalCobrado = sales.reduce((a, s) => a + Number(s.cobrado || 0), 0);
  const totalPorCobrar = sales.reduce((a, s) => a + Number(s.por_cobrar || 0), 0);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <Store size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Bazares y Expositores</h1>
            <p className="text-sm text-muted-foreground">
              Ediciones, stands, expositores y cobranza.
            </p>
          </div>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {sales.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground">Ediciones</div>
            <div className="text-2xl font-semibold text-foreground">{sales.length}</div>
          </div>
          <div className="rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground">Cobrado</div>
            <div className="text-2xl font-semibold text-foreground">{money(totalCobrado)}</div>
          </div>
          <div className="rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground">Por cobrar</div>
            <div className="text-2xl font-semibold text-foreground">{money(totalPorCobrar)}</div>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              active === t.key
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {error && <ErrorBox msg={error} />}

      {/* ── Material de apoyo (carga su propia lista) ─────── */}
      {active === 'material' && <MaterialPanel />}

      {/* ── Interesados por edición ──────────────────────── */}
      {active === 'interesados' && <InteresadosPorEdicion datos={interes} loading={loading} />}

      {/* ── Ventas por bazar ─────────────────────────────── */}
      {active === 'ventas' && (
        <div className="space-y-3">
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={verPasadas} onChange={(e) => setVerPasadas(e.target.checked)} className="rounded border-border" />
            Ver ediciones pasadas
          </label>
          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Edición</th>
                  <th className="text-left px-4 py-2 font-medium">Fecha</th>
                  <th className="text-right px-4 py-2 font-medium">Stands</th>
                  <th className="text-right px-4 py-2 font-medium">Esperado</th>
                  <th className="text-right px-4 py-2 font-medium">Cobrado</th>
                  <th className="text-right px-4 py-2 font-medium">Por cobrar</th>
                  <th className="text-right px-4 py-2 font-medium">Gastos</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {agruparPorMes(sales).map(([mes, filas]) => (
                  <MesGroup key={mes} mes={mes} filas={filas} onGasto={load}
                    onOpen={(s) => { setSelectedEdition(s); setActive('stands'); }} />
                ))}
              </tbody>
            </table>
            {!loading && !sales.length && (
              <Empty>{verPasadas ? 'Aún no hay ediciones registradas.' : 'No hay ediciones próximas. Activa «Ver ediciones pasadas» para el histórico.'}</Empty>
            )}
          </div>
        </div>
      )}

      {/* ── Stands de una edición ────────────────────────── */}
      {active === 'stands' && (
        <div className="space-y-3">
          <select
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
            value={selectedEdition?.edition_id ?? ''}
            onChange={async (e) => {
              const ed = sales.find((s) => s.edition_id === Number(e.target.value)) || null;
              setSelectedEdition(ed);
              if (ed) {
                setLoading(true);
                try { setStands(await api.stands(ed.edition_id)); }
                catch (err) { setError((err as Error).message); }
                finally { setLoading(false); }
              }
            }}
          >
            {sales.map((s) => (
              <option key={s.edition_id} value={s.edition_id}>
                {s.venue} — {fecha(s.start_date)}
              </option>
            ))}
          </select>

          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Stand</th>
                  <th className="text-left px-4 py-2 font-medium">Expositor</th>
                  <th className="text-left px-4 py-2 font-medium">Qué vende</th>
                  <th className="text-right px-4 py-2 font-medium">Cuota</th>
                  <th className="text-right px-4 py-2 font-medium">Pagado</th>
                  <th className="text-right px-4 py-2 font-medium">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {stands.map((s) => (
                  <tr key={s.assignment_id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono text-xs text-foreground">{s.stand_code || '—'}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        {s.logo_url ? (
                          <img src={s.logo_url} alt="" className="h-6 w-6 rounded object-cover" />
                        ) : null}
                        <span className="text-foreground">{s.exhibitor}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{s.vende || '—'}</td>
                    <td className="px-4 py-2 text-right">{money(s.cuota)}</td>
                    <td className="px-4 py-2 text-right">{money(s.pagado)}</td>
                    <td className={`px-4 py-2 text-right ${Number(s.saldo) > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {money(s.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && !stands.length && <Empty>Esta edición aún no tiene expositores asignados.</Empty>}
          </div>
        </div>
      )}

      {/* ── Padrón de expositores ────────────────────────── */}
      {active === 'expositores' && (
        <div className="space-y-3">
          <div className="relative max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
              placeholder="Buscar por nombre…"
              className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm text-foreground"
            />
          </div>
          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Expositor</th>
                  <th className="text-left px-4 py-2 font-medium">Qué vende</th>
                  <th className="text-left px-4 py-2 font-medium">Contacto</th>
                  <th className="text-right px-4 py-2 font-medium">Bazares</th>
                  <th className="text-right px-4 py-2 font-medium">Pagado</th>
                  <th className="text-right px-4 py-2 font-medium">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {exhibitors.map((x) => (
                  <tr key={x.exhibitor_id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        {x.logo_url ? (
                          <img src={x.logo_url} alt="" className="h-6 w-6 rounded object-cover" />
                        ) : null}
                        <span className="text-foreground">{x.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{x.vende || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{x.phone || x.email || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      {x.ediciones_asistidas}
                      <span className="text-muted-foreground">/{x.ediciones_participadas}</span>
                    </td>
                    <td className="px-4 py-2 text-right">{money(x.pagado_total)}</td>
                    <td className={`px-4 py-2 text-right ${Number(x.saldo_total) > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {money(x.saldo_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && !exhibitors.length && <Empty>Aún no hay expositores registrados.</Empty>}
          </div>
        </div>
      )}

      {/* ── Por cobrar ───────────────────────────────────── */}
      {active === 'solicitudes' && (
        <div className="space-y-3">
          {conNovedad > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <span>
                {conNovedad === 1 ? '1 persona que ya tenía respuesta pidió algo nuevo' : `${conNovedad} personas que ya tenían respuesta pidieron algo nuevo`}
                {' '}(rack, mesa extra, otro giro, otra fecha). Están hasta arriba.
              </span>
            </div>
          )}
          <div className="relative max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
              placeholder="Buscar por nombre, teléfono o lo que vende…"
              className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm text-foreground"
            />
          </div>
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          {solicitudes.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              Todavía no hay solicitudes. Cuando alguien le escriba al agente
              pidiendo un lugar, aparece aquí.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Quién</th>
                  <th className="text-left px-4 py-2 font-medium">Contacto</th>
                  <th className="text-left px-4 py-2 font-medium">Qué cuenta</th>
                  <th className="text-left px-4 py-2 font-medium">Última vez</th>
                  <th className="text-left px-4 py-2 font-medium">Curaduría</th>
                </tr>
              </thead>
              <tbody>
                {ordenadas.map((s) => (
                  <tr key={s.id} className="border-t border-border align-top hover:bg-muted/30">
                    <td className="px-4 py-2 text-foreground whitespace-nowrap">
                      {s.nombre || '—'}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      <div>{s.telefono || '—'}</div>
                      {s.email && <div className="text-xs">{s.email}</div>}
                    </td>
                    {/* `mensaje` acumula lo que ha contado en varias charlas:
                        se muestra completo, ahí está el contexto de la venta. */}
                    <td className="px-4 py-2 text-muted-foreground whitespace-pre-line max-w-md">
                      {(s.curacion_novedades?.length ?? 0) > 0 && (
                        <Novedades solicitud={s} onRevisado={recargarSolicitudes} />
                      )}
                      {s.mensaje || '—'}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {fecha(s.actualizado)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <Curaduria
                        solicitud={s}
                        ediciones={sales}
                        onDecidido={recargarSolicitudes}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </div>
      )}

      {active === 'cobrar' && (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Expositor</th>
                <th className="text-left px-4 py-2 font-medium">Contacto</th>
                <th className="text-left px-4 py-2 font-medium">Edición</th>
                <th className="text-right px-4 py-2 font-medium">Cuota</th>
                <th className="text-right px-4 py-2 font-medium">Pagado</th>
                <th className="text-right px-4 py-2 font-medium">Debe</th>
                <th className="text-left px-4 py-2 font-medium">Cobrar</th>
              </tr>
            </thead>
            <tbody>
              {debts.map((d) => (
                <DeudaRow key={d.assignment_id} d={d} />
              ))}
            </tbody>
          </table>
          {!loading && !debts.length && <Empty>Nadie debe nada. 🎉</Empty>}
        </div>
      )}
    </div>
  );
}


// ── Curaduría de una solicitud ────────────────────────────────────────
// Aprobar/Rechazar NO le escribe a la persona. Asienta la decisión y, si es
// rechazo, devuelve las fechas donde ese giro SÍ cabe. El mensaje lo redacta
// el agente y la administradora lo autoriza antes de que salga — por eso aquí
// se muestran las alternativas en vez de mandar nada.
function Curaduria({
  solicitud,
  ediciones,
  onDecidido,
}: {
  solicitud: Solicitud;
  ediciones: EditionSales[];
  onDecidido: () => void;
}) {
  const [enviando, setEnviando] = useState<'aprobar' | 'rechazar' | null>(null);
  const [resultado, setResultado] = useState<DecisionOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Una persona puede quedar aprobada para CUALQUIER edición, o para VARIAS.
  // Antes solo se podía elegir una y la amarraba a esa fecha (06-oct: Brenda,
  // aprobada para «cualquiera menos La Vista», quedó amarrada a Halloween).
  const [eligiendo, setEligiendo] = useState(false);
  const [modo, setModo] = useState<'cualquiera' | 'solo'>('cualquiera');
  const [elegidas, setElegidas] = useState<number[]>([]);
  const hoy = new Date().toISOString().slice(0, 10);
  const proximas = ediciones.filter((e) => e.start_date >= hoy);
  const alternar = (id: number) =>
    setElegidas((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  const decidir = async (decision: 'aprobar' | 'rechazar') => {
    setEnviando(decision);
    setError(null);
    try {
      const r = await api.decidirSolicitud({
        lead_id: solicitud.id,
        decision,
        // NO se manda el mensaje como giro: el backend usa el giro limpio que
        // ya conoce (mandar el mensaje completo pisaba giros correctos).
        ...(decision === 'aprobar'
          ? { ediciones: modo === 'cualquiera' ? ('cualquiera' as const) : elegidas }
          : {}),
      });
      setResultado(r);
      setEligiendo(false);
      onDecidido();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setEnviando(null);
    }
  };

  if (solicitud.curacion && !resultado && !eligiendo) {
    const aprobado = solicitud.curacion === 'aprobado';
    return (
      <div className="text-xs">
        <span
          className={[
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium',
            aprobado
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
          ].join(' ')}
        >
          {aprobado ? <Check size={12} /> : <X size={12} />}
          {aprobado ? 'Aprobado' : 'Rechazado'}
        </span>
        {aprobado && !solicitud.curacion_edicion && (
          <div className="mt-1 text-muted-foreground max-w-[14rem]">
            {solicitud.curacion_ediciones?.length
              ? <>Para: {solicitud.curacion_ediciones.join(' · ')}</>
              : <>Cualquier edición{solicitud.curacion_sedes_excluidas?.length
                  ? <> · menos {solicitud.curacion_sedes_excluidas.map((x) => x.split(',')[0]).join(', ')}</>
                  : null}</>}
          </div>
        )}
        {aprobado && (
          <button
            type="button"
            onClick={() => setEligiendo(true)}
            className="mt-1 text-primary hover:underline"
          >
            Cambiar ediciones
          </button>
        )}
        {solicitud.curacion_edicion && (
          <div className="mt-1 text-muted-foreground max-w-[14rem]">
            {solicitud.curacion_edicion}
            {solicitud.curacion_cuota && (
              <> · cuota {money(solicitud.curacion_cuota)}</>
            )}
            {solicitud.curacion_anticipo && (
              <> · aparta con {money(solicitud.curacion_anticipo)}</>
            )}
          </div>
        )}
        {solicitud.curacion_motivo && (
          <div className="mt-1 text-muted-foreground max-w-[13rem] whitespace-pre-line">
            {solicitud.curacion_motivo}
          </div>
        )}
        <div className="mt-0.5 text-muted-foreground">
          {solicitud.curacion_avisado ? 'Ya se le avisó' : 'Falta avisarle'}
        </div>
      </div>
    );
  }

  if (resultado) {
    return (
      <div className="text-xs max-w-[15rem]">
        <div className="font-medium text-foreground">
          {resultado.decision === 'aprobado' ? 'Aprobado' : 'Rechazado'}
        </div>
        {resultado.alternativas.length > 0 && (
          <div className="mt-1 text-muted-foreground">
            Fechas donde sí cabe:
            <ul className="mt-0.5 space-y-0.5">
              {resultado.alternativas.map((a) => (
                <li key={a.edition_id}>· {a.sede} — {fecha(a.fecha_inicio)}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-1 text-muted-foreground">
          Pídele al agente que le redacte el mensaje.
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {eligiendo ? (
        <div className="flex flex-col gap-1.5 rounded-md border border-border bg-card p-2 text-xs max-w-[17rem]">
          <span className="font-medium text-foreground">¿Para qué ediciones?</span>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name={`modo-${solicitud.id}`} checked={modo === 'cualquiera'}
                   onChange={() => setModo('cualquiera')} />
            Cualquier edición
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name={`modo-${solicitud.id}`} checked={modo === 'solo'}
                   onChange={() => setModo('solo')} />
            Solo estas:
          </label>
          {modo === 'solo' && (
            <div className="ml-5 flex flex-col gap-1 max-h-40 overflow-y-auto">
              {proximas.map((e) => (
                <label key={e.edition_id} className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={elegidas.includes(e.edition_id)}
                         onChange={() => alternar(e.edition_id)} />
                  <span className="truncate">{e.venue.split(',')[0]} — {fecha(e.start_date)}</span>
                </label>
              ))}
              {!proximas.length && <span className="text-muted-foreground">No hay ediciones próximas.</span>}
            </div>
          )}
          {solicitud.curacion_sedes_excluidas?.length ? (
            <span className="text-muted-foreground">
              Se respetan las plazas excluidas: {solicitud.curacion_sedes_excluidas.map((x) => x.split(',')[0]).join(', ')}.
            </span>
          ) : null}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => decidir('aprobar')}
              disabled={enviando !== null || (modo === 'solo' && !elegidas.length)}
              title={modo === 'solo' && !elegidas.length ? 'Elige al menos una edición' : 'Aprobar'}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 px-2 py-1 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
            >
              {enviando === 'aprobar' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Confirmar
            </button>
            <button
              type="button"
              onClick={() => { setEligiendo(false); setElegidas([]); setModo('cualquiera'); }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEligiendo(true)}
          disabled={enviando !== null}
          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 px-2 py-1 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
        >
          <Check size={12} />
          Aprobar
        </button>
      )}
      <button
        type="button"
        onClick={() => decidir('rechazar')}
        disabled={enviando !== null}
        className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 px-2 py-1 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
      >
        {enviando === 'rechazar' ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
        Rechazar
      </button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}


// ── Ventas por bazar: agrupado por mes ────────────────────────────────
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function etiquetaMes(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  return `${MESES[(m || 1) - 1]} ${y}`;
}

/** [["septiembre 2026", [...]], ...] en el orden en que vienen (el backend
 *  ya ordena por fecha). Los subtotales del mes se calculan aquí porque son
 *  la SUMA de filas que ya están en pantalla, no un total del negocio. */
function agruparPorMes(filas: EditionSales[]): Array<[string, EditionSales[]]> {
  const out: Array<[string, EditionSales[]]> = [];
  for (const s of filas) {
    const k = etiquetaMes(s.start_date);
    const last = out[out.length - 1];
    if (last && last[0] === k) last[1].push(s);
    else out.push([k, [s]]);
  }
  return out;
}

function MesGroup({ mes, filas, onOpen, onGasto }: {
  mes: string;
  filas: EditionSales[];
  onOpen: (s: EditionSales) => void;
  onGasto: () => void;
}) {
  const [abierto, setAbierto] = useState(true);
  const suma = (k: 'esperado' | 'cobrado' | 'por_cobrar' | 'costos') =>
    filas.reduce((acc, s) => acc + Number(s[k] || 0), 0);
  const cur = filas[0]?.currency || 'MXN';
  return (
    <>
      <tr className="border-t border-border bg-muted/30">
        <td colSpan={2} className="px-4 py-2">
          <button type="button" onClick={() => setAbierto((v) => !v)}
            className="inline-flex items-center gap-1 font-medium text-foreground capitalize">
            {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {mes}
            <span className="ml-1 text-xs text-muted-foreground normal-case">· {filas.length} edición{filas.length === 1 ? '' : 'es'}</span>
          </button>
        </td>
        <td className="px-4 py-2 text-right text-muted-foreground">{filas.reduce((a, s) => a + s.stands_tomados, 0)}</td>
        <td className="px-4 py-2 text-right text-muted-foreground">{money(suma('esperado'), cur)}</td>
        <td className="px-4 py-2 text-right font-medium text-foreground">{money(suma('cobrado'), cur)}</td>
        <td className={`px-4 py-2 text-right ${suma('por_cobrar') > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{money(suma('por_cobrar'), cur)}</td>
        <td className="px-4 py-2 text-right text-muted-foreground">{money(suma('costos'), cur)}</td>
        <td />
      </tr>
      {abierto && filas.map((s) => (
        <EdicionRow key={s.edition_id} s={s} onOpen={onOpen} onGasto={onGasto} />
      ))}
    </>
  );
}

function EdicionRow({ s, onOpen, onGasto }: {
  s: EditionSales; onOpen: (s: EditionSales) => void; onGasto: () => void;
}) {
  const [capturando, setCapturando] = useState(false);
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [tipo, setTipo] = useState('venue');
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const guardar = async () => {
    const n = Number(monto);
    if (!concepto.trim() || !(n > 0)) { setErr('Concepto y monto mayor a cero.'); return; }
    setGuardando(true); setErr(null);
    try {
      await api.crearGasto({ edition_id: s.edition_id, amount: n, concept: concepto.trim(), cost_type: tipo });
      setCapturando(false); setConcepto(''); setMonto('');
      onGasto();
    } catch (e) { setErr((e as Error).message); } finally { setGuardando(false); }
  };

  return (
    <>
      <tr className="border-t border-border hover:bg-muted/30">
        <td className="px-4 py-2 text-foreground cursor-pointer" onClick={() => onOpen(s)}>{s.venue}</td>
        <td className="px-4 py-2 text-muted-foreground">{fecha(s.start_date)}</td>
        <td className="px-4 py-2 text-right">
          {s.stands_tomados}{s.capacity ? <span className="text-muted-foreground">/{s.capacity}</span> : null}
        </td>
        <td className="px-4 py-2 text-right">{money(s.esperado, s.currency)}</td>
        <td className="px-4 py-2 text-right text-foreground">{money(s.cobrado, s.currency)}</td>
        <td className={`px-4 py-2 text-right ${Number(s.por_cobrar) > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{money(s.por_cobrar, s.currency)}</td>
        <td className="px-4 py-2 text-right text-muted-foreground">{money(s.costos, s.currency)}</td>
        <td className="px-2 py-2 text-right whitespace-nowrap">
          <button type="button" onClick={() => setCapturando((v) => !v)} title="Registrar un gasto de esta edición"
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50">
            <Plus size={12} /> Gasto
          </button>
        </td>
      </tr>
      {capturando && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={8} className="px-4 py-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <input value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Concepto (renta, montaje, difusión…)"
                className="rounded-md border border-border bg-background px-2 py-1 min-w-[16rem]" />
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1">
                <option value="venue">Renta del lugar</option>
                <option value="marketing">Publicidad y difusión</option>
                <option value="staff">Personal</option>
                <option value="setup">Montaje y mobiliario</option>
                <option value="permits">Permisos y trámites</option>
                <option value="other">Otro</option>
              </select>
              <input value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="Monto" inputMode="decimal"
                className="rounded-md border border-border bg-background px-2 py-1 w-28 text-right" />
              <button type="button" onClick={guardar} disabled={guardando}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 px-2 py-1 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50">
                {guardando ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Guardar
              </button>
              <button type="button" onClick={() => { setCapturando(false); setErr(null); }} className="text-xs text-muted-foreground hover:text-foreground">Cancelar</button>
              {err && <span className="text-xs text-destructive">{err}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


// ── Por cobrar: una deuda con sus pagos y sus botones ─────────────────
const METODO: Record<string, string> = { stripe: 'Stripe', transfer: 'Transferencia', cash: 'Efectivo' };

function DeudaRow({ d }: { d: DebtRow }) {
  const [abierto, setAbierto] = useState(false);
  const [enviando, setEnviando] = useState<'saldo' | 'anticipo' | null>(null);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);

  const enviar = async (modo: 'saldo' | 'anticipo') => {
    setEnviando(modo); setAviso(null);
    try {
      const r = await api.enviarLiga(d.assignment_id, modo);
      // El backend dice la verdad sobre la entrega: fuera de la ventana de
      // 24h, Meta la rechaza y aquí NO se marca como enviada.
      setAviso(r.envio.enviado
        ? { ok: true, texto: `Liga de ${modo} por ${money(r.monto)} enviada a ${d.phone}.` }
        : { ok: false, texto: r.envio.error || 'No se pudo enviar.' });
    } catch (e) {
      setAviso({ ok: false, texto: (e as Error).message });
    } finally { setEnviando(null); }
  };

  const anticipoPagado = Number(d.pagado) > 0;
  return (
    <>
      <tr className="border-t border-border hover:bg-muted/30">
        <td className="px-4 py-2 text-foreground">
          <button type="button" onClick={() => setAbierto((v) => !v)} className="inline-flex items-center gap-1">
            {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{d.exhibitor}
          </button>
        </td>
        <td className="px-4 py-2 text-muted-foreground">{d.phone || '—'}</td>
        <td className="px-4 py-2 text-muted-foreground">{d.edition}</td>
        <td className="px-4 py-2 text-right">{money(d.cuota)}</td>
        <td className="px-4 py-2 text-right">{money(d.pagado)}</td>
        <td className="px-4 py-2 text-right text-destructive font-medium">{money(d.saldo)}</td>
        <td className="px-4 py-2 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => enviar('saldo')} disabled={enviando !== null || !d.phone}
              title={d.phone ? 'Mandarle por WhatsApp la liga para liquidar' : 'Sin teléfono'}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/50 disabled:opacity-50">
              {enviando === 'saldo' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Liga de saldo
            </button>
            {!anticipoPagado && (
              <button type="button" onClick={() => enviar('anticipo')} disabled={enviando !== null || !d.phone}
                title="Reenviar la liga del anticipo"
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/50 disabled:opacity-50">
                {enviando === 'anticipo' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Anticipo
              </button>
            )}
          </div>
          {aviso && (
            <div className={`mt-1 text-xs ${aviso.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>{aviso.texto}</div>
          )}
        </td>
      </tr>
      {abierto && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={7} className="px-4 py-2">
            {d.pagos.length === 0 ? (
              <span className="text-xs text-muted-foreground">Sin pagos registrados todavía.</span>
            ) : (
              <table className="text-xs">
                <thead className="text-muted-foreground">
                  <tr><th className="text-left pr-4 py-1 font-medium">Cuándo</th><th className="text-right pr-4 py-1 font-medium">Monto</th><th className="text-left pr-4 py-1 font-medium">Método</th><th className="text-left pr-4 py-1 font-medium">Estado</th><th className="text-left py-1 font-medium">Referencia</th></tr>
                </thead>
                <tbody>
                  {d.pagos.map((p: PagoDetalle, i: number) => (
                    <tr key={i}>
                      <td className="pr-4 py-1 text-foreground whitespace-nowrap">{fecha(p.fecha)}</td>
                      <td className="pr-4 py-1 text-right">{money(p.monto)}</td>
                      <td className="pr-4 py-1">{METODO[p.metodo] || p.metodo || '—'}</td>
                      <td className="pr-4 py-1">{p.status}</td>
                      <td className="py-1 font-mono text-muted-foreground">{p.referencia ? `${p.referencia.slice(0, 22)}…` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


// ── Interesados por edición ───────────────────────────────────────────
// Pedido de la administradora (06-oct): «una lista por edición de las personas
// interesadas, no importa si no se ha cerrado… si ya se mandó la información,
// la liga de pago, si está pendiente de pago o pagada». La etapa la CALCULA el
// backend de la decisión, el apartado y los pagos: aquí solo se pinta.
const ETAPAS_ORDEN: Etapa[] = ['interesado', 'aprobado', 'informado', 'liga', 'anticipo', 'pagado', 'no_entra'];

const ETAPA_ESTILO: Record<Etapa, string> = {
  interesado: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  aprobado: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  informado: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
  liga: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  anticipo: 'bg-teal-500/15 text-teal-700 dark:text-teal-300',
  pagado: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  no_entra: 'bg-muted text-muted-foreground',
};

const ETAPA_CORTA: Record<Etapa, string> = {
  interesado: 'Interesados',
  aprobado: 'Falta avisarles',
  informado: 'Información enviada',
  liga: 'Pendiente de pago',
  anticipo: 'Anticipo pagado',
  pagado: 'Pagados',
  no_entra: 'No entran',
};

function EtapaPill({ etapa, texto }: { etapa: Etapa; texto: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${ETAPA_ESTILO[etapa]}`}>
      {texto}
    </span>
  );
}

function InteresadosPorEdicion({ datos, loading }: { datos: InteresEdicion[]; loading: boolean }) {
  const [abierta, setAbierta] = useState<number | null>(null);
  if (!loading && !datos.length) return <Empty>No hay ediciones próximas.</Empty>;
  // Van por fecha; la barra compara por cuál preguntan más y por cuál menos.
  const max = Math.max(1, ...datos.map((d) => d.total));
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Quién preguntó por cada edición y en qué va. Cuenta desde el primer mensaje, aunque todavía no haya nada cerrado.
      </p>
      {datos.map((d) => (
        <div key={d.edition_id} className="rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={() => setAbierta(abierta === d.edition_id ? null : d.edition_id)}
            className="w-full text-left px-4 py-3 flex flex-col gap-2 hover:bg-muted/40 rounded-lg"
            aria-expanded={abierta === d.edition_id}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">{d.sede.split(',')[0]} — {fecha(d.fecha_inicio)}</div>
                <div className="text-xs text-muted-foreground truncate">{d.edicion}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm font-semibold text-foreground tabular-nums">{d.total}</span>
                <span className="text-xs text-muted-foreground">interesados</span>
                {abierta === d.edition_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary/70" style={{ width: `${(d.total / max) * 100}%` }} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ETAPAS_ORDEN.filter((e) => d.por_etapa[e]).map((e) => (
                <EtapaPill key={e} etapa={e} texto={`${ETAPA_CORTA[e]}: ${d.por_etapa[e]}`} />
              ))}
            </div>
          </button>
          {abierta === d.edition_id && (
            <div className="border-t border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">Persona</th>
                    <th className="text-left font-medium px-4 py-2">Vende</th>
                    <th className="text-left font-medium px-4 py-2">En qué va</th>
                    <th className="text-right font-medium px-4 py-2">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {d.interesados.map((p: Interesado, i) => (
                    <tr key={`${p.lead_id ?? p.telefono}-${i}`} className="border-t border-border">
                      <td className="px-4 py-2">
                        <div className="text-foreground">{p.nombre || 'Sin nombre'}</div>
                        {p.telefono && <div className="text-xs text-muted-foreground tabular-nums">{p.telefono}</div>}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground max-w-[14rem] truncate">{p.giro || '—'}</td>
                      <td className="px-4 py-2"><EtapaPill etapa={p.etapa} texto={p.etapa_texto} /></td>
                      <td className="px-4 py-2 text-right tabular-nums">{p.saldo ? money(p.saldo) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.interesados.length && <Empty>Nadie ha preguntado por esta edición todavía.</Empty>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
